import { useEffect, useState } from "react";
import { AlertTriangle, NotebookPen, Pencil, Plus, Trash2 } from "lucide-react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Disclaimer, Loader, ModuleOff, PageHeader, Reveal } from "@/components/common";
import { AilmentForm, emptyAilment, ZONE_LABELS } from "@/components/AilmentForm";
import { SliderField } from "@/components/SliderField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const LEVEL = { leve: "bg-food-soft text-food-ink", moderado: "bg-streak-soft text-streak-ink", alto: "bg-red-50 text-red-700" };

const SymptomDialog = ({ ailment, onClose, onSaved }) => {
  const [s, setS] = useState({ pain: ailment?.intensity ?? 3, mobility: 3, notes: "" });
  const save = async () => {
    try { await api.post(`/ailments/${ailment.id}/symptoms`, s); toast.success("Síntoma anotado"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!ailment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="symptom-dialog">
        <DialogHeader><DialogTitle className="font-display">¿Cómo está hoy: {ailment?.name}?</DialogTitle></DialogHeader>
        <div className="space-y-6 py-2">
          <SliderField label="Dolor ahora" value={s.pain} min={0} max={10} onChange={(v) => setS({ ...s, pain: v })} testid="symptom-pain" />
          <SliderField label="Movilidad" value={s.mobility} min={1} max={5} onChange={(v) => setS({ ...s, mobility: v })} testid="symptom-mobility" hint="1 = muy limitada · 5 = normal" />
          <Textarea placeholder="Notas: qué has hecho, qué lo ha empeorado…" value={s.notes} onChange={(e) => setS({ ...s, notes: e.target.value })} className="rounded-2xl" data-testid="symptom-notes" />
        </div>
        <button onClick={save} data-testid="symptom-submit-btn" className="w-full rounded-full bg-care py-3.5 font-semibold text-white transition-transform active:scale-95">Guardar en el diario</button>
      </DialogContent>
    </Dialog>
  );
};

const AilmentDialog = ({ initial, onClose, onSaved }) => {
  const [a, setA] = useState(initial);
  useEffect(() => setA(initial), [initial]);
  const save = async () => {
    if (!a.name.trim()) return toast.error("Describe la dolencia");
    try {
      const body = { zone: a.zone, name: a.name, intensity: a.intensity, since: a.since, notes: a.notes, active: a.active };
      a.id ? await api.put(`/ailments/${a.id}`, body) : await api.post("/ailments", body);
      toast.success("Dolencia guardada. Tu plan se ha adaptado.");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!initial} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-lg" data-testid="ailment-dialog">
        <DialogHeader><DialogTitle className="font-display">{a?.id ? "Editar dolencia" : "Nueva dolencia"}</DialogTitle></DialogHeader>
        {a && <AilmentForm a={a} set={(p) => setA({ ...a, ...p })} />}
        <button onClick={save} data-testid="ailment-submit-btn" className="mt-2 w-full rounded-full bg-care py-3.5 font-semibold text-white transition-transform active:scale-95">Guardar</button>
      </DialogContent>
    </Dialog>
  );
};

const AilmentCard = ({ a, onLog, onEdit, onDelete, onToggle, i }) => (
  <Reveal delay={i * 0.06} className="card-clay p-5 sm:p-6">
    <div className="flex items-start justify-between gap-3" data-testid={`ailment-card-${i}`}>
      <div className="min-w-0">
        <p className="eyebrow text-care-ink">{ZONE_LABELS[a.zone]} · desde {fmtDate(a.since)}</p>
        <h3 className="mt-1 text-lg font-bold">{a.name}</h3>
        <span className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${LEVEL[a.advice.level]}`} data-testid={`ailment-level-${i}`}>Dolor {a.intensity}/10 · {a.advice.level} · {a.advice.trend}</span>
      </div>
      <div className="flex items-center gap-1">
        <Switch checked={a.active} onCheckedChange={onToggle} data-testid={`ailment-active-switch-${i}`} />
        <button onClick={onEdit} className="rounded-full p-2 hover:bg-stone-100" data-testid={`ailment-edit-${i}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={onDelete} className="rounded-full p-2 hover:bg-stone-100" data-testid={`ailment-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <p className="mt-4 text-sm font-semibold text-ink">{a.advice.message}</p>
    <ul className="mt-2 space-y-1.5 text-sm text-stone-600">{a.advice.tips.map((t) => <li key={t}>· {t}</li>)}</ul>
    {a.advice.consult && (
      <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm text-red-800" data-testid={`ailment-consult-alert-${i}`}>
        <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Te recomendamos consultar a un profesional</p>
        <ul className="mt-2 space-y-1">{a.advice.red_flags.map((r) => <li key={r}>· {r}</li>)}</ul>
      </div>
    )}
    {a.symptoms.length > 1 && (
      <div className="mt-4 h-28" data-testid={`ailment-chart-${i}`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={a.symptoms}><XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} /><YAxis domain={[0, 10]} hide /><Tooltip labelFormatter={fmtDate} formatter={(v) => [`${v}/10`, "Dolor"]} />
            <Line type="monotone" dataKey="pain" stroke="#6366F1" strokeWidth={3} dot={{ r: 3 }} /></LineChart>
        </ResponsiveContainer>
      </div>
    )}
    <button onClick={onLog} data-testid={`ailment-log-symptom-${i}`} className="mt-4 flex items-center gap-2 rounded-full bg-care-soft px-4 py-2.5 text-sm font-semibold text-care-ink transition-transform active:scale-95">
      <NotebookPen className="h-4 w-4" /> Anotar cómo estoy hoy
    </button>
  </Reveal>
);

export default function Ailments() {
  const { me } = useAuth();
  const on = me.modules?.ailments;
  const [list, setList] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/ailments").then((r) => setList(r.data));
  useEffect(() => { if (on) load(); }, [on]);
  if (!on) return <ModuleOff module="ailments" />;
  if (!list) return <Loader />;
  const body = (a, p) => ({ zone: a.zone, name: a.name, intensity: a.intensity, since: a.since, notes: a.notes, active: a.active, ...p });
  const del = async (a) => { if (window.confirm(`¿Eliminar "${a.name}" y su diario?`)) { await api.delete(`/ailments/${a.id}`); load(); } };
  const toggle = async (a, v) => { await api.put(`/ailments/${a.id}`, body(a, { active: v })); load(); };

  return (
    <div data-testid="ailments-page" className="space-y-5">
      <PageHeader eyebrow="Dolencias" tone="care" title="Cuida lo que te duele" subtitle="Registra lesiones o molestias: tu entreno evita los ejercicios que las cargan.">
        <button onClick={() => setEditing(emptyAilment())} data-testid="ailment-add-btn" className="mt-4 flex items-center gap-2 rounded-full bg-care px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95"><Plus className="h-4 w-4" /> Nueva dolencia</button>
      </PageHeader>
      <Disclaimer />
      {!list.length && <p className="rounded-3xl border border-dashed border-stone-300 p-8 text-center text-stone-600" data-testid="ailments-empty">No tienes dolencias registradas.</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        {list.map((a, i) => <AilmentCard key={a.id} a={a} i={i} onLog={() => setLogFor(a)} onEdit={() => setEditing(a)} onDelete={() => del(a)} onToggle={(v) => toggle(a, v)} />)}
      </div>
      <SymptomDialog key={logFor?.id} ailment={logFor} onClose={() => setLogFor(null)} onSaved={() => { setLogFor(null); load(); }} />
      <AilmentDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
